/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : Jul 27, 2025
Folder          : src/components/button
File            : ButtonCompDark.tsx
Description     : 
    This reusable component is a dark button.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/


// Import React Systems
import React, 
        {   ChangeEvent, 
            useState,useRef } from 'react';
import { Link } from "react-router-dom";

// Imports MUI Components
import { TextField, Typography, Box, Stack, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Imports Functions
import { textfieldErrors } from "../../../functions/textfieldErrors";

const TextFieldComp = ({ 
  id,
  label,
  type,
  value,
  color,
  placeholder,
  onChange,
  state,
  special,
  search,
  requires,
  maxLength,
  readOnly,
  onBlur,
  error,
  helperText,
  nonClickable,
  grayWhenReadOnly,
  showPasswordToggle,
  showPassword,
  onTogglePassword,
  min,
  max,
  step,
  onKeyDown,
}) => {
 
  // Initialization
  const [input, setInput] = useState(value);
  const [inputError, setInputError] = useState(false);
  const [errorText, setErrorText] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")

  // Helper function to extract text from label (handles both string and JSX)
  const getLabelText = (labelValue) => {
    if (typeof labelValue === 'string') {
      return labelValue;
    }
    if (labelValue && typeof labelValue === 'object') {
      // If it's a React element, try to extract text content
      if (labelValue.props && labelValue.props.children) {
        const children = labelValue.props.children;
        if (typeof children === 'string') {
          return children;
        }
        if (Array.isArray(children)) {
          // Find the first string child
          const textChild = children.find(child => typeof child === 'string');
          if (textChild) return textChild;
        }
        if (typeof children === 'object' && children.props) {
          return getLabelText(children);
        }
      }
    }
    return "This field";
  };

  // 🔹 Validate on blur or change
  const handleBlurInternal = () => {
    // Call external onBlur handler first
    if (onBlur) {
      onBlur();
    }
    
    // Fallback validation if no external validation provided
    // Only run if helperText is not provided (undefined) AND error prop is not set
    // This prevents fallback validation when parent component is managing validation
    // If helperText is provided (even empty string), parent is managing validation
    if (!error && helperText === undefined) {
      const raw = (value === undefined || value === null) ? input : value;
      const str = typeof raw === "string" ? raw : String(raw || "");
      if (requires && str.trim() === "") {
        setInputError(true);
        const labelText = getLabelText(label);
        setErrorText(`${labelText} is required`);
      } else {
        setInputError(false);
        setErrorText("");
      }
    } else {
      // If external validation is provided (via error or helperText prop), use that instead
      // Clear internal error to avoid conflicts
      setInputError(false);
      setErrorText("");
    }
  };

  // Styling for the text field label
  const styleText = {
    color: '#2C251E',
    fontWeight: {
      xs : '600',
      sm : '600',
      md : '100',
      lg : '100',
      xl : '100',
    },
    fontSize: '14px',
    paddingY: requires ? '5px' : '8px',
  };

  return (
    <div>
      <Box textAlign="start">
        {/* TextField Container */}
        <Stack direction="column">
            {/* Text */}
            <Typography variant="h3" sx={styleText}>
              <Stack direction="row" spacing={2}>
                {label}
                {requires && (
                  <Typography
                    sx={{
                      color: "#9F4223",
                      padding: "0 3px"
                    }}
                  >
                    *
                  </Typography>
                )} 
              </Stack>
            </Typography>
      
            {/* TextField */}
            <TextField
                placeholder={
                  requires || requires === "Patient" ? placeholder : 
                  !requires ? placeholder : 
                  null
                }
                readOnly={readOnly}
                maxLength={maxLength}
                required={requires}
                onBlur={handleBlurInternal}
                error={Boolean(error || inputError)}
                id={id}
                type={type}
                value={value}
                variant="outlined"
                helperText={helperText || errorText}
                color={color}
                FormHelperTextProps={{
                  sx: { color: 'error.main' }
                }}
                InputProps={{
                  maxLength: maxLength,
                  readOnly: state,
                  endAdornment: showPasswordToggle && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={onTogglePassword}
                        edge="end"
                        sx={{
                          margin: "0 0 0 0",
                          padding: 0,
                          color: '#2C251E',
                          '&:hover': {
                            backgroundColor: 'rgba(197, 165, 114, 0.2)',
                          },
                        }}
                      >
                        {value ? (!showPassword ? <VisibilityOff /> : <Visibility />) : null}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '10px',
                    fontSize: '14px',
                    color: special ? "rgba(44, 37, 30, .7)" : null,
                    backgroundColor: (state || grayWhenReadOnly) ? "#f4f4f4" : "#ffffff",
                    '& input': {
                      color: (state || grayWhenReadOnly) ? "#666666" : undefined
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 
                        special ? "rgba(222, 171, 120, 0.4)" : 
                        'rgba(197, 165, 114, 0.4)',
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 
                        special ? "rgba(44, 37, 30, .4)" :
                        (error || inputError) ? 'error.main' :
                        'rgba(197, 165, 114, 0.4)',
                    },
                    '&:hover': {
                      bgcolor: 
                        special ? "none" : '#f0f0f0ff',
                    },
                    pointerEvents: nonClickable ? "none" : undefined,
                    '& input::placeholder': {
                      color: "#2C251E",
                      opacity: .6,
                      fontSize: "12px"
                    },
                  },
                }}
                inputProps={{
                  maxLength: maxLength,
                  tabIndex: nonClickable ? -1 : undefined,
                  readOnly: nonClickable ? true : undefined,
                  min: min !== undefined ? min : undefined,
                  max: max !== undefined ? max : undefined,
                  step: step !== undefined ? step : undefined,
                }}
                onChange={onChange}
                onKeyDown={onKeyDown}
                sx={{
                    width: '100%',
                    borderRadius: '10px',
                }}
            />
      
            </Stack>
        </Box>
    </div>
  )
}

export default TextFieldComp
